import { cloneElement, isValidElement, useRef, type ReactNode } from "react"

import { mergeProps, useButton } from "react-aria"
import {
  OverlayArrow,
  Tooltip as AriaTooltip,
  TooltipTrigger as AriaTooltipTrigger,
  type TooltipTriggerComponentProps,
  type TooltipProps as AriaTooltipProps,
} from "react-aria-components"
import { twMerge } from "tailwind-merge"

type TooltipProps = {
  /** The trigger element for the tooltip. For now, should be a react-aria
   * Button, but in the future we could consider using useTrigger and
   * useTooltipTrigger in the future. */
  children: ReactNode
  content: ReactNode
  delay?: TooltipTriggerComponentProps["delay"]
  showArrow?: boolean
  tooltipProps?: Omit<AriaTooltipProps, "className"> & {
    className?: string
  }
  triggerProps?: Partial<TooltipTriggerComponentProps>
}

/**
 * Alternative Tooltip implementation that uses useButton hook for better button integration.
 */

const TooltipTrigger = ({
  children,
  ...props
}: TooltipTriggerComponentProps) => {
  const triggerRef = useRef<HTMLElement>(null)
  const { buttonProps } = useButton(props, triggerRef)

  if (!isValidElement(children)) {
    // eslint-disable-next-line no-console
    console.error("TooltipTrigger must be a valid React element")
    return null
  }

  return cloneElement(
    children,
    mergeProps(
      { ...buttonProps, type: undefined },
      {
        tabIndex: 0,
        ...children.props,
      },
      {
        ref: triggerRef,
      }
    )
  )
}

/**
 * A popup that appears when a user hovers or focuses on an element. Useful for
 * providing additional context or information about an element such as a button
 * or icon in a way that doesn't clutter the UI.
 *
 * ```tsx
 * import { Tooltip } from "@tokenterminal/ui/overlays/tooltip/Tooltip"
 *
 * <Tooltip content="Learn more">
 *   <IconButton icon={Info} label="Learn more" />
 * </Tooltip>
 * ```
 */

export const Tooltip = ({
  children,
  content,
  delay = 400,
  showArrow,
  tooltipProps: { className: tooltipClassName, ...tooltipProps } = {},
  triggerProps,
}: TooltipProps) => {
  return (
    <AriaTooltipTrigger delay={delay} {...triggerProps}>
      {children}
      <AriaTooltip
        className={twMerge("tt-tooltip", tooltipClassName)}
        {...tooltipProps}
      >
        {showArrow && (
          <OverlayArrow>
            <svg
              className="tt-overlay-arrow"
              width={8}
              height={8}
              viewBox="0 0 8 8"
            >
              <path d="M0 0 L4 4 L8 0" />
            </svg>
          </OverlayArrow>
        )}
        {content}
      </AriaTooltip>
    </AriaTooltipTrigger>
  )
}

Tooltip.Trigger = TooltipTrigger
